/**
 * @fileoverview Modal para visualização de snapshot de premissas
 * @module components/modals/ViewPremissasSnapshotModal
 * 
 * SPRINT 3: Permite ao usuário ver EXATAMENTE quais premissas foram usadas
 * para gerar um valuation específico, resolvendo problema de reprodutibilidade
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { 
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import type { PremissasSnapshot } from '../../types/version';

// ============================================================================
// TYPES
// ============================================================================

interface ViewPremissasSnapshotModalProps {
  /** Controla visibilidade do modal */
  open: boolean;
  
  /** Callback ao fechar modal */
  onClose: () => void;
  
  /** Snapshot de premissas a exibir */
  snapshot: PremissasSnapshot;
  
  /** Nome da versão DRE (para contexto) */
  versionName: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formata número como percentual
 */
const formatPercent = (value: number, decimals = 2): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Componente de seção com título
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        {children}
      </Paper>
    </Box>
  );
}

/**
 * Componente de linha de parâmetro
 */
function ParamRow({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        fontWeight={highlight ? 700 : 600}
        color={highlight ? 'primary.main' : 'text.primary'}
      >
        {value}
      </Typography>
    </Stack>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ViewPremissasSnapshotModal({
  open,
  onClose,
  snapshot,
  versionName,
}: ViewPremissasSnapshotModalProps) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon />
              Premissas Usadas
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {versionName}
            </Typography>
          </Box>
          <Chip 
            label={formatDistanceToNow(snapshot.timestamp, {
              addSuffix: true,
              locale: ptBR,
            })}
            size="small"
            color="info"
          />
        </Stack>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Premissas Macroeconômicas */}
          <Section title="Premissas Macroeconômicas">
            <Stack spacing={1}>
              <ParamRow label="Taxa Selic (Rf)" value={formatPercent(snapshot.macro.taxaSelic)} highlight />
              <ParamRow label="Prêmio de Risco de Mercado (ERP)" value={formatPercent(snapshot.macro.premioRiscoMercado)} />
              <ParamRow label="Prêmio de Risco Brasil (CRP)" value={formatPercent(snapshot.macro.premioRiscoBrasil)} />
            </Stack>
          </Section>

          {/* Premissas Sétima */}
          <Section title="Premissas - Sétima">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ParamRow label="Beta" value={snapshot.setima.beta.toFixed(2)} highlight />
                <ParamRow label="WACC" value={formatPercent(snapshot.setima.wacc)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom display="block">
                  Crescimento Anual:
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1 }}>
                  {Object.entries(snapshot.setima.crescimento).map(([year, value]) => (
                    <ParamRow key={year} label={year} value={formatPercent(value as number)} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom display="block">
                  Margem EBITDA:
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 1 }}>
                  {Object.entries(snapshot.setima.margemEBITDA).map(([year, value]) => (
                    <Typography key={year} variant="caption">
                      {year}: <strong>{formatPercent(value as number)}</strong>
                    </Typography>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Section>

          {/* Premissas 555 Studios */}
          <Section title="Premissas - 555 Studios">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ParamRow label="Beta" value={snapshot.studios555.beta.toFixed(2)} highlight />
                <ParamRow label="WACC" value={formatPercent(snapshot.studios555.wacc)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom display="block">
                  Crescimento Anual:
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1 }}>
                  {Object.entries(snapshot.studios555.crescimento).map(([year, value]) => (
                    <ParamRow key={year} label={year} value={formatPercent(value as number)} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom display="block">
                  Margem EBITDA:
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 1 }}>
                  {Object.entries(snapshot.studios555.margemEBITDA).map(([year, value]) => (
                    <Typography key={year} variant="caption">
                      {year}: <strong>{formatPercent(value as number)}</strong>
                    </Typography>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Section>

          {/* Descontos e Participação */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Section title="Descontos de Negociação">
                <Stack spacing={1}>
                  <ParamRow label="Falta de Liquidez (DLOM)" value={formatPercent(snapshot.discounts.dlom)} />
                  <ParamRow label="Falta de Controle (DLOC)" value={formatPercent(snapshot.discounts.dloc)} />
                  <Divider />
                  <ParamRow label="Total" value={formatPercent(snapshot.discounts.total)} highlight />
                </Stack>
              </Section>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Section title="Participação dos Sócios">
                <Stack spacing={1}>
                  <ParamRow label="Savio Bertolani" value={formatPercent(snapshot.stake.savioBertolani)} highlight />
                  <ParamRow label="Caio Pires" value={formatPercent(snapshot.stake.caioPires)} />
                  <ParamRow label="Vicente Padula" value={formatPercent(snapshot.stake.vicentePadula)} />
                  <ParamRow label="Bruno Faria" value={formatPercent(snapshot.stake.brunoFaria)} />
                </Stack>
              </Section>
            </Grid>
          </Grid>

          {/* Info box */}
          <Paper sx={{ p: 2, bgcolor: 'info.50', borderLeft: 4, borderColor: 'info.main' }}>
            <Typography variant="caption" color="text.secondary">
              <LightbulbIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              <strong>Como usar:</strong> Estas são as premissas EXATAS que foram usadas para calcular o valuation 
              desta versão DRE. Para reproduzir o valuation, restaure esta versão DRE e configure as premissas 
              com estes mesmos valores.
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" startIcon={<CloseIcon />}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
